import moment from 'moment';
import type { ApplicantFormData } from '../types/api';
import type { EmploymentStatus } from '../../App/types';
import { parseCalendarDateUtc } from '../../../utils/date-helpers';

// Map boolean value to 'yes'/'no'/null
const mapBooleanToYesNo = (
  value: boolean | null | undefined,
): 'yes' | 'no' | null => {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return null;
};

// Map 'yes'/'no'/null to boolean
const mapYesNoToBoolean = (
  value: 'yes' | 'no' | 'doNotWishToProvide' | null | undefined,
): boolean | null => {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return null;
};

// Convert date string to moment object if value exists
const mapDateToMoment = (
  value: string | null | undefined,
): moment.Moment | string | null => {
  if (value == null || value === '') {
    return value === '' ? '' : null;
  }
  return moment(value);
};

// Map employment status ID to form value
const mapEmploymentStatusIdToValue = (
  employmentStatusId: string | null | undefined,
  employmentStatuses: Array<EmploymentStatus>,
): string | null => {
  if (!employmentStatusId || !employmentStatuses.length) {
    return null;
  }

  const status = employmentStatuses.find((s) => s.id === employmentStatusId);
  if (!status) {
    return null;
  }

  // Map the status name to the expected form values
  const nameToValueMap = {
    Employed: 'employed',
    'Not Employed': 'notEmployed',
    Retired: 'retired',
    Student: 'student',
    Other: 'other',
  };

  return nameToValueMap[status.name] || null;
};

// Map employment status form value to ID
const mapEmploymentStatusValueToId = (
  employmentStatusValue: string | null | undefined,
  employmentStatuses: Array<EmploymentStatus>,
): string | null => {
  if (!employmentStatusValue || !employmentStatuses.length) {
    return null;
  }

  // Map the form value to status name
  const valueToNameMap = {
    employed: 'Employed',
    notEmployed: 'Not Employed',
    retired: 'Retired',
    student: 'Student',
    other: 'Other',
  };

  const statusName = valueToNameMap[employmentStatusValue];
  if (!statusName) {
    return null;
  }

  const status = employmentStatuses.find((s) => s.name === statusName);
  return status?.id || null;
};

// Map a minor from API format to form format
const mapMinorToFormValues = (minor) => ({
  minorId: minor?.id || null,
  applicantId: minor?.applicantId || null,
  minorLegalFirstName: minor?.firstName || '',
  minorMiddleName: minor?.middleName || '',
  minorLastName: minor?.lastName || '',
  minorPreferredName: minor?.preferredName || '',
  minorSuffix: minor?.suffixId || null,
  minorDateOfBirth: parseCalendarDateUtc(minor?.birthday) || '',
  minorSocialSecurityNumber: minor?.socialSecurityNumber || '',
  minorAlienRegistrationNumber: minor?.alienRegistrationNumber || '',
  minorGender: minor?.gender || null,
  minorAffordableRelationship: minor?.affordableRelationshipId || null,
  minorEthnicity: minor?.ethnicity || null,
  minorRace: minor?.race || null,
  minorMultiRace: minor?.multiRace || [],
  minorRaceSource: minor?.raceSource || null,
  // 'notASudent' is a legacy value this form saved before FES-1109; read it as 'n_s'
  isMinorAStudent:
    minor?.studentType === 'notASudent' ? 'n_s' : minor?.studentType || null,
  possessJointCustodyOfMinor: minor?.jointCustody || null,
  isMinorDisabled: minor?.disabilities?.disabilityStatus || null,
  requiredReasonableAccommodation:
    minor?.disabilities?.reasonableAccommodation || null,
});

const mapAnyMinorsToFormValue = (
  anyMinors: boolean | null | undefined,
  minorCount: number,
): 'yes' | 'no' => {
  return anyMinors === true || minorCount > 0 ? 'yes' : 'no';
};

// An expected child has no SSN, demographics, schooling or custody arrangement
// of its own to report, so the resident portal lists it with these answers. It
// records them step by step as the applicant moves through the application, so
// a child from an application the applicant has not finished would otherwise
// open here with required fields blank. A blank answer takes the default and
// any other answer is kept.
const UNBORN_CHILD_ANSWERS = {
  minorSocialSecurityNumber: '000000000',
  minorGender: 'doNotWishToProvide',
  minorEthnicity: 'doNotWishToProvide',
  minorMultiRace: ['notWishToProvide'],
  minorRaceSource: 'C-Customer Provided',
  isMinorAStudent: 'n_s',
  possessJointCustodyOfMinor: 'no',
};

const isBlankAnswer = (value) =>
  value == null || value === '' || (Array.isArray(value) && !value.length);

const fillUnbornChildAnswers = (minorValues) =>
  Object.entries(UNBORN_CHILD_ANSWERS).reduce(
    (values, [field, answer]) =>
      isBlankAnswer(values[field]) ? { ...values, [field]: answer } : values,
    minorValues,
  );

const mapMinorsToFormValue = (
  minors: MinorInformation[],
  anyMinors: boolean | null | undefined,
  unbornChildRelationshipId: string | null | undefined,
): MinorInformation[] => {
  if (minors.length === 0 && anyMinors) return [mapMinorToFormValues({})];
  return minors.map((minor) => {
    const minorValues = mapMinorToFormValues(minor);
    const isUnbornChild =
      !!unbornChildRelationshipId &&
      minor?.affordableRelationshipId === unbornChildRelationshipId;

    return isUnbornChild ? fillUnbornChildAnswers(minorValues) : minorValues;
  });
};

export const mapApplicantDataToFormValues = ({
  applicantFormData,
  employmentStatuses = [],
  canSignElectronically = false,
  unbornChildRelationshipId = null,
  isPendingResidentApplicant = false,
}: {
  applicantFormData: ApplicantFormData | undefined,
  employmentStatuses: Array<EmploymentStatus>,
  canSignElectronically: boolean,
  unbornChildRelationshipId?: string | null,
  isPendingResidentApplicant: boolean,
}) => {
  if (!applicantFormData?.applicantInformation) {
    return {};
  }

  const {
    applicantInformation,
    employmentInformation,
    residentialInformation,
    householdInformation,
    additionalInformation,
    signingMethod,
  } = applicantFormData;

  const {
    basicInformation,
    registrationAndLegalInformation,
    contactInformation,
    studentAndAssistanceInformation,
    disabilityStatus,
    veteranStatus,
  } = applicantInformation;

  // Compute minors section values
  const areThereAnyMinors = mapAnyMinorsToFormValue(
    householdInformation?.anyMinors,
    householdInformation?.minors?.length ?? 0,
  );
  const minors = mapMinorsToFormValue(
    householdInformation?.minors ?? [],
    householdInformation?.anyMinors,
    unbornChildRelationshipId,
  );

  return {
    // Basic Information
    legalFirstName: basicInformation?.firstName || '',
    middleName: basicInformation?.middleName || '',
    lastName: basicInformation?.lastName || '',
    suffix: basicInformation?.suffixId || null,
    preferredName: basicInformation?.preferredName || '',
    dateOfBirth: parseCalendarDateUtc(basicInformation?.birthday) || '',
    gender: basicInformation?.gender || null,
    ethnicity: basicInformation?.ethnicity || null,
    race: basicInformation?.race || null,
    multiRace: basicInformation?.multiRace || [],
    raceSource: basicInformation?.raceSource || null,
    veteran: veteranStatus || null,
    disabilityQuestion: disabilityStatus || null,

    // Registration and Legal
    driversLicenseStateId: registrationAndLegalInformation?.stateIdNumber || '',
    driversLicenseIdState: registrationAndLegalInformation?.stateIdOrigin || '',
    ssnOrTin: registrationAndLegalInformation?.socialSecurityNumber || '',
    passportNumber: registrationAndLegalInformation?.passportNumber || '',
    passportCountry: registrationAndLegalInformation?.passportCountry || '',
    alienRegistrationNumber:
      registrationAndLegalInformation?.alienRegistration || '',

    // Contact Information
    phoneNumber: contactInformation?.phoneNumber || '',
    emailAddress: contactInformation?.emailAddress || '',

    // Employment Information
    employmentStatus: mapEmploymentStatusIdToValue(
      employmentInformation?.employmentStatusId,
      employmentStatuses,
    ),
    anticipateChangeEmployer: mapBooleanToYesNo(
      employmentInformation?.anticipateChangeEmployer,
    ),

    // Current Job
    currentJobPosition: employmentInformation?.currentEmployer?.position,
    currentJobAnnualSalary:
      employmentInformation?.currentEmployer?.annualSalary,
    currentJobStartDate: mapDateToMoment(
      employmentInformation?.currentEmployer?.from,
    ),
    currentJobEmployerName: employmentInformation?.currentEmployer?.name,
    currentJobEmployerPhone:
      employmentInformation?.currentEmployer?.phoneNumber,
    currentJobSupervisorName:
      employmentInformation?.currentEmployer?.supervisorName,
    currentJobStreetAddress:
      employmentInformation?.currentEmployer?.streetAddress,
    currentJobCity: employmentInformation?.currentEmployer?.city,
    currentJobState: employmentInformation?.currentEmployer?.state,
    currentJobZipCode: employmentInformation?.currentEmployer?.zipCode,

    // Second Job
    secondJobDoesNotApply:
      employmentInformation.hasSecondEmployer === false ? 'on' : '',
    secondJobPosition: employmentInformation?.secondEmployer?.position,
    secondJobAnnualSalary: employmentInformation?.secondEmployer?.annualSalary,
    secondJobStartDate: mapDateToMoment(
      employmentInformation?.secondEmployer?.from,
    ),
    secondJobEmployerName: employmentInformation?.secondEmployer?.name,
    secondJobEmployerPhone: employmentInformation?.secondEmployer?.phoneNumber,
    secondJobSupervisorName:
      employmentInformation?.secondEmployer?.supervisorName,
    secondJobStreetAddress:
      employmentInformation?.secondEmployer?.streetAddress,
    secondJobCity: employmentInformation?.secondEmployer?.city,
    secondJobState: employmentInformation?.secondEmployer?.state,
    secondJobZipCode: employmentInformation?.secondEmployer?.zipCode,

    // Previous Job
    previousJobDoesNotApply:
      employmentInformation.hasPreviousEmployer === false ? 'on' : '',
    previousJobPosition: employmentInformation?.previousEmployer?.position,
    previousJobAnnualSalary:
      employmentInformation?.previousEmployer?.annualSalary,
    previousJobStartDate: mapDateToMoment(
      employmentInformation?.previousEmployer?.from,
    ),
    previousJobEndDate: mapDateToMoment(
      employmentInformation?.previousEmployer?.to,
    ),
    previousJobEmployerName: employmentInformation?.previousEmployer?.name,
    previousJobEmployerPhone:
      employmentInformation?.previousEmployer?.phoneNumber,
    previousJobSupervisorName:
      employmentInformation?.previousEmployer?.supervisorName,
    previousJobStreetAddress:
      employmentInformation?.previousEmployer?.streetAddress,
    previousJobCity: employmentInformation?.previousEmployer?.city,
    previousJobState: employmentInformation?.previousEmployer?.state,
    previousJobZipCode: employmentInformation?.previousEmployer?.zipCode,

    // Current Residence
    currentResidenceStreetAddress:
      residentialInformation?.currentResidence?.street,
    currentResidenceCity: residentialInformation?.currentResidence?.city,
    currentResidenceState: residentialInformation?.currentResidence?.state,
    currentResidenceZipCode: residentialInformation?.currentResidence?.zipCode,
    currentResidenceMoveInDate: mapDateToMoment(
      residentialInformation?.currentResidence?.moveInDate,
    ),
    currentResidenceMonthlyPayments:
      residentialInformation?.currentResidence?.monthlyPay,
    currentResidenceOwnOrRent:
      residentialInformation?.currentResidence?.ownOrRent,
    currentResidenceLenderName:
      residentialInformation?.currentResidence?.ownOrRent === 'own'
        ? residentialInformation?.currentResidence?.lenderName
        : undefined,
    currentResidenceLenderPhone:
      residentialInformation?.currentResidence?.ownOrRent === 'own'
        ? residentialInformation?.currentResidence?.lenderPhoneNumber
        : undefined,
    currentResidenceLandlordName:
      residentialInformation?.currentResidence?.ownOrRent === 'rent'
        ? residentialInformation?.currentResidence
            ?.landlordOrCondoOrApartmentName
        : undefined,
    currentResidenceLandlordPhone:
      residentialInformation?.currentResidence?.ownOrRent === 'rent'
        ? residentialInformation?.currentResidence
            ?.landlordOrCondoOrApartmentNamePhoneNumber
        : undefined,
    currentResidenceReason:
      residentialInformation?.currentResidence?.ownOrRent === 'other'
        ? residentialInformation?.currentResidence
            ?.doNotCurrentlyOwnRentExplanation
        : undefined,
    currentResidenceReasonForMoving:
      residentialInformation?.currentResidence?.reasonForMoving,

    // Previous Residence
    previousResidenceStreetAddress:
      residentialInformation?.previousResidence?.street,
    previousResidenceCity: residentialInformation?.previousResidence?.city,
    previousResidenceState: residentialInformation?.previousResidence?.state,
    previousResidenceZipCode:
      residentialInformation?.previousResidence?.zipCode,
    previousResidenceMoveInDate: mapDateToMoment(
      residentialInformation?.previousResidence?.moveInDate,
    ),
    previousResidenceMonthlyPayments:
      residentialInformation?.previousResidence?.monthlyPay,
    previousResidenceOwnOrRent:
      residentialInformation?.previousResidence?.ownOrRent,
    previousResidenceLenderName:
      residentialInformation?.previousResidence?.ownOrRent === 'own'
        ? residentialInformation?.previousResidence?.lenderName
        : undefined,
    previousResidenceLenderPhone:
      residentialInformation?.previousResidence?.ownOrRent === 'own'
        ? residentialInformation?.previousResidence?.lenderPhoneNumber
        : undefined,
    previousResidenceLandlordName:
      residentialInformation?.previousResidence?.ownOrRent === 'rent'
        ? residentialInformation?.previousResidence
            ?.landlordOrCondoOrApartmentName
        : undefined,
    previousResidenceLandlordPhone:
      residentialInformation?.previousResidence?.ownOrRent === 'rent'
        ? residentialInformation?.previousResidence
            ?.landlordOrCondoOrApartmentNamePhoneNumber
        : undefined,
    previousResidenceReason:
      residentialInformation?.previousResidence?.ownOrRent === 'other'
        ? residentialInformation?.previousResidence
            ?.doNotCurrentlyOwnRentExplanation
        : undefined,
    previousResidenceReasonForMoving:
      residentialInformation?.previousResidence?.reasonForMoving,

    // Second Previous Residence
    secondPreviousResidenceStreetAddress:
      residentialInformation?.secondPreviousResidence?.street,
    secondPreviousResidenceCity:
      residentialInformation?.secondPreviousResidence?.city,
    secondPreviousResidenceState:
      residentialInformation?.secondPreviousResidence?.state,
    secondPreviousResidenceZipCode:
      residentialInformation?.secondPreviousResidence?.zipCode,
    secondPreviousResidenceMoveInDate: mapDateToMoment(
      residentialInformation?.secondPreviousResidence?.moveInDate,
    ),
    secondPreviousResidenceMonthlyPayments:
      residentialInformation?.secondPreviousResidence?.monthlyPay,
    secondPreviousResidenceOwnOrRent:
      residentialInformation?.secondPreviousResidence?.ownOrRent,
    secondPreviousResidenceLenderName:
      residentialInformation?.secondPreviousResidence?.ownOrRent === 'own'
        ? residentialInformation?.secondPreviousResidence?.lenderName
        : undefined,
    secondPreviousResidenceLenderPhone:
      residentialInformation?.secondPreviousResidence?.ownOrRent === 'own'
        ? residentialInformation?.secondPreviousResidence?.lenderPhoneNumber
        : undefined,
    secondPreviousResidenceLandlordName:
      residentialInformation?.secondPreviousResidence?.ownOrRent === 'rent'
        ? residentialInformation?.secondPreviousResidence
            ?.landlordOrCondoOrApartmentName
        : undefined,
    secondPreviousResidenceLandlordPhone:
      residentialInformation?.secondPreviousResidence?.ownOrRent === 'rent'
        ? residentialInformation?.secondPreviousResidence
            ?.landlordOrCondoOrApartmentNamePhoneNumber
        : undefined,
    secondPreviousResidenceReason:
      residentialInformation?.secondPreviousResidence?.ownOrRent === 'other'
        ? residentialInformation?.secondPreviousResidence
            ?.doNotCurrentlyOwnRentExplanation
        : undefined,
    secondPreviousResidenceReasonForMoving:
      residentialInformation?.secondPreviousResidence?.reasonForMoving,

    // Household Members
    maritalStatus: householdInformation?.maritalStatus,
    spouseFirstName: householdInformation?.spouse?.firstName,
    spouseMiddleName: householdInformation?.spouse?.middleName,
    spouseLastName: householdInformation?.spouse?.lastName,
    spousePreferredName: householdInformation?.spouse?.preferredName,
    adultsOver18: mapBooleanToYesNo(householdInformation?.anyAdultsOver18),
    howManyAdditionalOccupants: householdInformation?.adultsOver18Count,
    additionalOccupantsDescription:
      householdInformation?.specialAdultsDescription,
    anticipateChangeInHousehold: mapBooleanToYesNo(
      householdInformation?.anticipateChangeInHousehold,
    ),
    howManyPregnancy: householdInformation?.pregnancyCount ?? 0,
    howManyAdoption: householdInformation?.adoptionCount ?? 0,
    howManyFoster: householdInformation?.fosterChildrenCount ?? 0,
    doesUnitNeedToBeAccessible: mapBooleanToYesNo(
      householdInformation?.needsUnitAccessible,
    ),

    // Minors Section
    areThereAnyMinors,
    minors,

    // Additional Information

    howDidYouHearAboutProperty: additionalInformation?.referralTypeId,
    hasBeenConvictedOfFelony: mapBooleanToYesNo(
      additionalInformation?.hasConvictedFelony,
    ),
    hasFiledBankruptcy: mapBooleanToYesNo(
      additionalInformation?.hasFiledBankruptcy,
    ),
    bankruptcyTypeFiled: additionalInformation?.bankruptcyTypeFiled,
    bankruptcyDateFiled: mapDateToMoment(
      additionalInformation?.bankruptcyDateFiled,
    ),
    hasBankruptcyBeenDischarged: mapBooleanToYesNo(
      additionalInformation?.hasBankruptcyBeenDischarged,
    ),

    // Student and Assistance Information

    areYouAStudent: mapBooleanToYesNo(
      studentAndAssistanceInformation?.isStudent,
    ),
    enrollmentStatus: studentAndAssistanceInformation?.enrollmentStatus,
    tanfAssistance: mapBooleanToYesNo(
      studentAndAssistanceInformation?.TANFAssistance,
    ),
    jobTraining: mapBooleanToYesNo(
      studentAndAssistanceInformation?.jobTraining,
    ),
    singleParentOrDependentChild: mapBooleanToYesNo(
      studentAndAssistanceInformation?.singleParentOrDependantChild,
    ),
    marriedJointReturn: mapBooleanToYesNo(
      studentAndAssistanceInformation?.marriedJointReturn,
    ),
    fosterProgram: mapBooleanToYesNo(
      studentAndAssistanceInformation?.fosterProgram,
    ),

    // Signing Method
    signingMethod:
      signingMethod ??
      (canSignElectronically && !isPendingResidentApplicant ? '' : 'manual'),
  };
};

// Helper to convert empty string to null
const emptyToNull = (value: string | null | undefined): string | null => {
  if (value === '' || value === undefined) return null;
  return value;
};

// Convert moment object or date string to date string, or null
const mapMomentToDateString = (
  value: moment.Moment | string | null | undefined,
): string | null => {
  if (value == null || value === '') {
    return null;
  }
  // Check if it's a moment object
  if (moment.isMoment(value)) {
    return value.format('YYYY-MM-DD');
  }
  // If it's already a string, return it (empty strings already handled above)
  if (typeof value === 'string') {
    return value;
  }
  return null;
};

// Helper to build employer information from form values
const buildEmployerInformation = (prefix: string, values: any) => {
  const hasData =
    values[`${prefix}Position`] ||
    values[`${prefix}AnnualSalary`] ||
    values[`${prefix}StartDate`] ||
    values[`${prefix}EmployerName`] ||
    values[`${prefix}EmployerPhone`] ||
    values[`${prefix}SupervisorName`] ||
    values[`${prefix}StreetAddress`] ||
    values[`${prefix}City`] ||
    values[`${prefix}State`] ||
    values[`${prefix}ZipCode`];

  if (!hasData) {
    return undefined;
  }

  return {
    position: emptyToNull(values[`${prefix}Position`]),
    annualSalary:
      values[`${prefix}AnnualSalary`] != null
        ? Number(values[`${prefix}AnnualSalary`])
        : null,
    from: mapMomentToDateString(values[`${prefix}StartDate`]),
    to: mapMomentToDateString(values[`${prefix}EndDate`]),
    name: emptyToNull(values[`${prefix}EmployerName`]),
    phoneNumber: emptyToNull(values[`${prefix}EmployerPhone`]),
    supervisorName: emptyToNull(values[`${prefix}SupervisorName`]),
    streetAddress: emptyToNull(values[`${prefix}StreetAddress`]),
    city: emptyToNull(values[`${prefix}City`]),
    state: emptyToNull(values[`${prefix}State`]),
    zipCode: emptyToNull(values[`${prefix}ZipCode`]),
  };
};

// Helper to build residence information from form values
const buildResidenceInformation = (prefix: string, values: any) => {
  const ownOrRent = values[`${prefix}OwnOrRent`] || null;

  return {
    street: emptyToNull(values[`${prefix}StreetAddress`]),
    city: emptyToNull(values[`${prefix}City`]),
    state: emptyToNull(values[`${prefix}State`]),
    zipCode: emptyToNull(values[`${prefix}ZipCode`]),
    moveInDate: mapMomentToDateString(values[`${prefix}MoveInDate`]),
    monthlyPay:
      values[`${prefix}MonthlyPayments`] != null
        ? Number(values[`${prefix}MonthlyPayments`])
        : null,
    ownOrRent,
    lenderName:
      ownOrRent === 'own' ? emptyToNull(values[`${prefix}LenderName`]) : null,
    lenderPhoneNumber:
      ownOrRent === 'own' ? emptyToNull(values[`${prefix}LenderPhone`]) : null,
    landlordOrCondoOrApartmentName:
      ownOrRent === 'rent'
        ? emptyToNull(values[`${prefix}LandlordName`])
        : null,
    landlordOrCondoOrApartmentNamePhoneNumber:
      ownOrRent === 'rent'
        ? emptyToNull(values[`${prefix}LandlordPhone`])
        : null,
    doNotCurrentlyOwnRentExplanation:
      ownOrRent === 'other' ? emptyToNull(values[`${prefix}Reason`]) : null,
    reasonForMoving: emptyToNull(values[`${prefix}ReasonForMoving`]),
  };
};

const getDeletedMinors = (initialValues, formValues) => {
  return (initialValues.minors ?? [])
    .filter(
      (minor) =>
        !formValues.minors.some((fMinor) => fMinor.minorId === minor.minorId),
    )
    .map((minor) => ({
      id: minor.minorId,
      applicantId: minor.applicantId,
      deleted: true,
    }));
};

export const mapFormValuesToApplicantData = ({
  formValues,
  employmentStatuses = [],
  initialValues,
}: {
  formValues: any,
  employmentStatuses?: Array<EmploymentStatus>,
}): ApplicantFormData => {
  // Basic Information
  const basicInformation = {
    firstName: emptyToNull(formValues.legalFirstName),
    middleName: emptyToNull(formValues.middleName),
    lastName: emptyToNull(formValues.lastName),
    suffixId: formValues.suffix || null,
    preferredName: emptyToNull(formValues.preferredName),
    birthday: mapMomentToDateString(formValues.dateOfBirth),
    gender: formValues.gender || null,
    ethnicity: formValues.ethnicity || null,
    race: formValues.race || null,
    multiRace: formValues.multiRace || null,
    raceSource: formValues.raceSource || null,
  };

  // Registration and Legal Information
  const registrationAndLegalInformation = {
    stateIdNumber: emptyToNull(formValues.driversLicenseStateId),
    stateIdOrigin: formValues.driversLicenseIdState || null,
    socialSecurityNumber: emptyToNull(formValues.ssnOrTin),
    alienRegistration: emptyToNull(formValues.alienRegistrationNumber),
    passportNumber: emptyToNull(formValues.passportNumber),
    passportCountry: formValues.passportCountry || null,
  };

  // Contact Information
  const contactInformation = {
    phoneNumber: emptyToNull(formValues.phoneNumber),
    emailAddress: emptyToNull(formValues.emailAddress),
  };

  // Student and Assistance Information
  const studentAndAssistanceInformation = {
    isStudent: mapYesNoToBoolean(formValues.areYouAStudent),
    enrollmentStatus: formValues.enrollmentStatus || null,
    TANFAssistance: mapYesNoToBoolean(formValues.tanfAssistance),
    jobTraining: mapYesNoToBoolean(formValues.jobTraining),
    singleParentOrDependantChild: mapYesNoToBoolean(
      formValues.singleParentOrDependentChild,
    ),
    marriedJointReturn: mapYesNoToBoolean(formValues.marriedJointReturn),
    fosterProgram: mapYesNoToBoolean(formValues.fosterProgram),
  };

  // Applicant Information
  const applicantInformation = {
    basicInformation,
    registrationAndLegalInformation,
    contactInformation,
    studentAndAssistanceInformation,
    disabilityStatus: formValues.disabilityQuestion || null,
    veteranStatus: formValues.veteran || null,
  };

  // Employment Information
  const employmentStatusId = mapEmploymentStatusValueToId(
    formValues.employmentStatus,
    employmentStatuses,
  );

  const hasSecondEmployer =
    formValues.secondJobDoesNotApply !== 'on' &&
    formValues.secondJobDoesNotApply !== true;
  const hasPreviousEmployer =
    formValues.previousJobDoesNotApply !== 'on' &&
    formValues.previousJobDoesNotApply !== true;

  // Build employer information - check if "doesNotApply" is set
  // If checkbox is 'on', there's no employer. Otherwise, check if there's data.
  const secondEmployerData = buildEmployerInformation('secondJob', formValues);
  const secondEmployer = hasSecondEmployer ? secondEmployerData : undefined;

  const previousEmployerData = buildEmployerInformation(
    'previousJob',
    formValues,
  );
  const previousEmployer = hasPreviousEmployer
    ? previousEmployerData
    : undefined;

  const employmentInformation = {
    hasSecondEmployer,
    hasPreviousEmployer,
    employmentStatusId,
    currentEmployer: buildEmployerInformation('currentJob', formValues),
    secondEmployer,
    previousEmployer,
    anticipateChangeEmployer: mapYesNoToBoolean(
      formValues.anticipateChangeEmployer,
    ),
  };

  // Residential Information
  const residentialInformation = {
    currentResidence: buildResidenceInformation('currentResidence', formValues),
    previousResidence: buildResidenceInformation(
      'previousResidence',
      formValues,
    ),
    secondPreviousResidence: buildResidenceInformation(
      'secondPreviousResidence',
      formValues,
    ),
  };

  // Household Information
  const spouse =
    formValues.spouseFirstName ||
    formValues.spouseMiddleName ||
    formValues.spouseLastName ||
    formValues.spousePreferredName
      ? {
          firstName: emptyToNull(formValues.spouseFirstName),
          middleName: emptyToNull(formValues.spouseMiddleName),
          lastName: emptyToNull(formValues.spouseLastName),
          preferredName: emptyToNull(formValues.spousePreferredName),
        }
      : undefined;

  const deletedMinors = getDeletedMinors(initialValues, formValues);

  const minors = (formValues.minors || [])
    .map((minor: any) => ({
      id: minor.minorId || null,
      applicantId: minor.applicantId || null,
      firstName: emptyToNull(minor.minorLegalFirstName),
      middleName: emptyToNull(minor.minorMiddleName),
      lastName: emptyToNull(minor.minorLastName),
      suffixId: minor.minorSuffix || null,
      preferredName: emptyToNull(minor.minorPreferredName),
      birthday: mapMomentToDateString(minor.minorDateOfBirth),
      socialSecurityNumber: emptyToNull(minor.minorSocialSecurityNumber),
      alienRegistrationNumber: emptyToNull(minor.minorAlienRegistrationNumber),
      gender: minor.minorGender || null,
      affordableRelationshipId: minor.minorAffordableRelationship || null,
      ethnicity: minor.minorEthnicity || null,
      race: minor.minorRace || null,
      multiRace: minor.minorMultiRace || null,
      raceSource: minor.minorRaceSource || null,
      studentType: minor.isMinorAStudent || null,
      jointCustody: minor.possessJointCustodyOfMinor || null,
      disabilities:
        minor.isMinorDisabled || minor.requiredReasonableAccommodation
          ? {
              disabilityStatus: minor.isMinorDisabled || null,
              reasonableAccommodation:
                minor.requiredReasonableAccommodation || null,
            }
          : undefined,
    }))
    .concat(deletedMinors);

  const householdInformation = {
    maritalStatus: formValues.maritalStatus || null,
    spouse,
    anyAdultsOver18: mapYesNoToBoolean(formValues.adultsOver18),
    adultsOver18Count:
      formValues.howManyAdditionalOccupants != null
        ? Number(formValues.howManyAdditionalOccupants)
        : null,
    specialAdultsDescription: emptyToNull(
      formValues.additionalOccupantsDescription,
    ),
    anticipateChangeInHousehold: mapYesNoToBoolean(
      formValues.anticipateChangeInHousehold,
    ),
    pregnancyCount:
      formValues.howManyPregnancy != null
        ? Number(formValues.howManyPregnancy)
        : null,
    adoptionCount:
      formValues.howManyAdoption != null
        ? Number(formValues.howManyAdoption)
        : null,
    fosterChildrenCount:
      formValues.howManyFoster != null
        ? Number(formValues.howManyFoster)
        : null,
    needsUnitAccessible: mapYesNoToBoolean(
      formValues.doesUnitNeedToBeAccessible,
    ),
    anyMinors: mapYesNoToBoolean(formValues.areThereAnyMinors),
    minors,
  };

  // Additional Information
  const additionalInformation = {
    referralTypeId: formValues.howDidYouHearAboutProperty || null,
    hasConvictedFelony: mapYesNoToBoolean(formValues.hasBeenConvictedOfFelony),
    hasFiledBankruptcy: mapYesNoToBoolean(formValues.hasFiledBankruptcy),
    hasBankruptcyBeenDischarged: mapYesNoToBoolean(
      formValues.hasBankruptcyBeenDischarged,
    ),
    bankruptcyTypeFiled: formValues.bankruptcyTypeFiled || null,
    bankruptcyDateFiled: mapMomentToDateString(formValues.bankruptcyDateFiled),
  };

  return {
    applicantInformation,
    employmentInformation,
    residentialInformation,
    householdInformation,
    additionalInformation,
    signingMethod:
      formValues.signingMethod === '' ? null : formValues.signingMethod || null,
  };
};
